import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, BarChart3, CheckCircle2, User, ShieldCheck } from 'lucide-react';
import { useIsAdmin } from '../../hooks/useIsAdmin';

/**
 * Mobile bottom navigation per design_handoff_mobile_ux/README.md.
 * Default tab order: Home · Performance · Track · Profile.
 *
 * KAN-3113 (admin Phase 3): admins get a 5th "Admin" tab appended. The
 * conditional tab keeps non-admin users on the spec'd 4-column layout — the
 * grid switches to grid-cols-5 only when `useIsAdmin()` resolves to true.
 *
 * Spec details:
 *   - 56px tall, paddingBottom = env(safe-area-inset-bottom)
 *   - Equal columns, 20px stroked icon + 9.5px label
 *   - Active tab: brighter --fg text, thicker stroke (2 vs 1.6),
 *     and a 28×2 brand-orange underline at the top with a soft glow
 *     (0 0 8px rgba(251,146,60,0.4))
 *   - "Motion confirms, never performs" — instant tab switches, no springs
 */
const BASE_TABS = [
  { id: 'home',        label: 'Home',        icon: Home,         path: '/'            },
  { id: 'performance', label: 'Performance', icon: BarChart3,    path: '/performance' },
  { id: 'track',       label: 'Track',       icon: CheckCircle2, path: '/track'       },
  { id: 'profile',     label: 'Profile',     icon: User,         path: '/profile'     },
] as const;

const ADMIN_TAB = {
  id: 'admin',
  label: 'Admin',
  icon: ShieldCheck,
  path: '/admin',
} as const;

export const BottomTabBar: React.FC = () => {
  const { pathname } = useLocation();
  const { isAdmin } = useIsAdmin();
  const tabs: ReadonlyArray<{
    id: string;
    label: string;
    icon: typeof Home;
    path: string;
  }> = isAdmin ? [...BASE_TABS, ADMIN_TAB] : BASE_TABS;

  const isActive = (path: string) =>
    path === '/' ? pathname === '/' : pathname.startsWith(path);

  return (
    <nav
      aria-label="Bottom navigation"
      className="fixed bottom-0 inset-x-0 z-40 md:hidden pb-[env(safe-area-inset-bottom)] bg-surface-chrome border-t border-hairline-strong"
      style={{ transform: 'translateZ(0)' }}
    >
      <div
        className={`grid h-[56px] ${isAdmin ? 'grid-cols-5' : 'grid-cols-4'}`}
      >
        {tabs.map(({ id, label, icon: Icon, path }) => {
          const active = isActive(path);
          return (
            <NavLink
              key={id}
              to={path}
              end={path === '/'}
              aria-label={label}
              aria-current={active ? 'page' : undefined}
              className="flex flex-col items-center justify-center gap-[3px] relative bg-transparent border-0 no-underline"
            >
              {/* 28×2 active indicator with brand glow */}
              <span
                className={`absolute top-0 h-[2px] w-[28px] rounded-[1px] bg-brand transition-opacity duration-150 ${
                  active
                    ? 'opacity-100 shadow-[0_0_8px_rgba(251,146,60,0.4)]'
                    : 'opacity-0'
                }`}
                aria-hidden="true"
              />
              <Icon
                className={`w-5 h-5 transition-colors duration-150 ${
                  active ? 'text-fg' : 'text-fg-dim'
                }`}
                strokeWidth={active ? 2 : 1.6}
                aria-hidden="true"
              />
              <span
                className={`font-plex text-[9.5px] font-semibold tracking-[0.02em] leading-none transition-colors duration-150 ${
                  active ? 'text-fg' : 'text-fg-dim'
                }`}
              >
                {label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomTabBar;
